import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SocketService } from '../service/socket.service';

/*
 * @description Room mapping for socket
 * Note: Counter offer rooms are now handled directly by component-level socket service
 * using the new hierarchical WEBSOCKET_ROOMS constants from websocket-room.constants.ts
 */
export const ROOM_MAPPING: { [key: string]: string } = {
    '/product-procurement/:id': 'CREATE_COUNTER_OFFER', // Legacy - handled by joinVendorRoom()
};

@Component({
    selector: 'app-socket-route-manager',
    template: '', // No template needed - this is a service component
    standalone: true,
})
export class SocketRouteManagerComponent implements OnInit, OnDestroy {
    private currentRoomId: string | null = null;
    private routerSubscription: Subscription = new Subscription();

    /*
     * @description Constructor for SocketRouteManagerComponent
     * @param router - Router for navigation
     * @param socketService - SocketService for socket management
     */
    constructor(private readonly router: Router, private readonly socketService: SocketService) {}

    /*
     * @description Method to initialize the component
     */
    ngOnInit(): void {
        // Subscribe to router events for socket room management
        this.routerSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
            this.handleRouteChange();
        });

        // Handle initial route on component initialization
        this.handleRouteChange();
    }

    /*
     * @description Method to destroy the component
     */
    ngOnDestroy(): void {
        // Leave current room if exists
        if (this.currentRoomId && this.socketService.getSocket()) {
            this.socketService.leaveRoom(this.currentRoomId);
        }

        // Clean up subscription
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
    }

    /*
     * @description Method to handle route change
     */
    private handleRouteChange(): void {
        if (!this.socketService.getSocket()) {
            return;
        }

        const currentRoute = this.router.url.split('?')[0]; // Get route without query params
        const queryParams = this.getQueryParams();
        const roomSlug = this.getRoomSlug(currentRoute, queryParams);

        // Leave previous room if exists
        if (this.currentRoomId) {
            this.socketService.leaveRoom(this.currentRoomId);
            this.currentRoomId = null;
        }

        let roomId: string | undefined;

        if (roomSlug) {
            switch (roomSlug) {
                case 'CREATE_COUNTER_OFFER':
                    // Counter offer rooms are now handled directly by item-table component
                    // with event-scoped isolation. Skip global room management here.

                    break;

                default:
                    // Handle other room types
                    if (Object.keys(queryParams).length > 0) {
                        const roomUniqueIdentifier = this.getRoomUniqueIdentifiers(roomSlug, queryParams);
                        roomId = `${roomSlug}_${roomUniqueIdentifier}`;
                    } else {
                        roomId = roomSlug;
                    }

                    this.socketService.joinRoom(roomId);
                    this.currentRoomId = roomId;
                    break;
            }
        }
    }

    /*
     * @description Method to get query parameters
     * @returns { [key: string]: any }
     */
    private getQueryParams(): { [key: string]: any } {
        const params: { [key: string]: any } = {};

        // Get query parameters from current route
        const urlTree = this.router.parseUrl(this.router.url);
        const queryParams = urlTree.queryParams;
        Object.assign(params, queryParams);

        return params;
    }

    /*
     * @description Method to get room slug
     * @param route - Route
     * @param queryParams - Query parameters
     * @returns string | undefined
     */
    private getRoomSlug(route: string, queryParams: { [key: string]: any }): string | undefined {
        for (const key of Object.keys(ROOM_MAPPING)) {
            // If there are no dynamic parameters (":" not present), compare directly
            if (!key.includes(':')) {
                if (route === key) {
                    return ROOM_MAPPING[key];
                }
                continue;
            }

            const routePattern = key.replace(/:\w+/g, '[^/]+');
            const regex = new RegExp(`^${routePattern}$`);
            if (regex.test(route)) {
                return ROOM_MAPPING[key];
            }
        }
        return undefined;
    }

    /*
     * @description Method to get room unique identifiers
     * @param roomSlug - Room slug
     * @param obj - Object
     * @returns string | null
     */
    private getRoomUniqueIdentifiers(roomSlug: string, obj: { [key: string]: any }): string | null {
        switch (roomSlug) {
            case 'CREATE_COUNTER_OFFER':
                // For counter offers, we only need vendor ID
                return obj?.['vendorId'] || null;
            default:
                return null;
        }
    }

    /*
     * @description Method to get current vendor ID
     * @returns string | null
     */
    private getCurrentVendorId(): string | null {
        // Get vendor ID from session storage
        const vendorData = localStorage.getItem('VendorData');
        if (vendorData) {
            try {
                const parsedData = JSON.parse(vendorData);
                return parsedData?.id || null;
            } catch (error) {
                return null;
            }
        }
        return null;
    }
}
